// Shared client-side helper for streaming daily metric records into the
// MongoDB historical warehouse (POST /api/warehouse/daily).
//
// Both source pages (Monthly Rolling Projections + Projections vs Consumption)
// auto-fire this on every refresh. Server-side upsert keyed on (date, sku,
// channel) handles idempotency — re-sending the same numbers is a no-op.
//
// Records carry only the metrics that the calling page actually has, so a
// Rolling refresh sends `projected_units` + `sales_units` (no dispatched);
// a Consumption refresh sends all three. The backend merges into whatever
// the other source already wrote that day without overwriting non-supplied
// metrics.

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const todayISODate = () => new Date().toISOString().slice(0, 10);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('radar_session_token')}`,
});

/**
 * Push records to the warehouse. Silent by design — fire-and-forget; failures
 * log to console only (this is a background sync, not user-facing).
 *
 * Records shape:
 *   {
 *     date:             'yyyy-mm-dd',
 *     channel:          'Amazon',
 *     sku:              'My SKU',
 *     asin:             'B0...',          // optional
 *     brand:            'Wellcore',       // optional
 *     projected_units:  Number | null,    // omit field if not known
 *     sales_units:      Number | null,
 *     dispatched_units: Number | null,
 *     source:           'rolling' | 'consumption',
 *     source_month:     0..11,
 *     source_year:      Number,
 *   }
 */
export const pushWarehouseRecords = async (records) => {
  if (!records || records.length === 0) return null;
  try {
    const res = await axios.post(
      `${API}/warehouse/daily`,
      { records },
      { headers: authHeaders() },
    );
    // Light dev signal — quiet in production builds.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info('[warehouse]', records.length, 'records →', res.data);
    }
    return res.data;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[warehouse] sync failed', e?.response?.data || e.message);
    return null;
  }
};
